/** A password (or passphrase) field with a see/unsee toggle.
 *
 * Ported from research-istok `apps/istok-classroom/src/ui/controls.tsx` at commit
 * b45a73e64276bc5edc4a7f8d795fcf370f72c55e (`PasswordInput`), rewritten onto this app's
 * tokens: `.field`, `--control-h`, `--line`, `--ink-2`. A passphrase typed on two devices has
 * to match exactly, so being able to look at it is not a nicety here.
 */
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

export function PasswordInput({
  label,
  hint,
  value,
  onChange,
  autoComplete,
  required,
  minLength,
  name,
  invalid,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  /** `current-password` where an existing one is typed, `new-password` where one is chosen.
   *  Required, because the wrong one has the manager filling the old password into a field
   *  meant to set a new one. */
  autoComplete: "current-password" | "new-password";
  required?: boolean;
  minLength?: number;
  name?: string;
  /** The hint then says what is wrong, and the field is marked invalid for assistive tech. */
  invalid?: boolean;
}) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);
  const id = useId();
  const hintId = `${id}-hint`;
  const action = shown ? t("field.hidePassword") : t("field.showPassword");
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium" htmlFor={id}>
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          name={name}
          className="field min-h-[var(--control-h)] w-full pr-14"
          type={shown ? "text" : "password"}
          autoComplete={autoComplete}
          /* A revealed password is still a password: no autocorrect, no capitalisation, no
             spell-check underline — all three of which a plain text input would apply. */
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          value={value}
          required={required}
          minLength={minLength}
          aria-invalid={invalid || undefined}
          aria-describedby={hint ? hintId : undefined}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          onClick={() => setShown((v) => !v)}
          aria-pressed={shown}
          aria-label={action}
          className="absolute inset-y-0 right-0 flex w-[var(--control-h)] items-center justify-center text-[color:var(--ink-2)] hover:text-[color:var(--ink)]"
        >
          <svg aria-hidden viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6">
            <path d="M1.8 12S5.4 5.5 12 5.5 22.2 12 22.2 12 18.6 18.5 12 18.5 1.8 12 1.8 12Z" />
            <circle cx="12" cy="12" r="3.2" />
            {shown && <path d="M4 20 20 4" />}
          </svg>
        </button>
      </div>
      {hint && (
        <p id={hintId} role={invalid ? "alert" : undefined} className={invalid ? "text-sm text-[color:var(--danger)]" : "text-xs text-[color:var(--ink-2)]"}>
          {hint}
        </p>
      )}
    </div>
  );
}
